import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import useDisclosure from '../hooks/useDisclosure';
import { selectFormHasUnsavedChanges } from '../redux/reducers/SessionReducer';
import ConfirmationDialog from './ConfirmationDialog';
import Iconify from './Iconify';
import Scrollbar from './Scrollbar';

// ---------------------------------------------------------------

interface StyledModalProps {
  open: boolean;
  handleClose: () => void;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  hideCloseButton?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  fullScreen?: boolean;
  isLoading?: boolean;
  children?: React.ReactNode;
  keepMounted?: boolean;
  showCloseModalWithUnsavedDataConfirmation?: boolean;
  withoutScroll?: boolean;
  sx?: object;
}

export default function StyledModal({
  open,
  handleClose,
  title,
  subtitle,
  hideCloseButton = false,
  maxWidth,
  fullScreen = false,
  isLoading = false,
  children,
  keepMounted = false,
  showCloseModalWithUnsavedDataConfirmation,
  withoutScroll,
  sx,
  ...rest
}: StyledModalProps) {
  const { t } = useTranslation();

  const fixedHeader = title || subtitle || !hideCloseButton;

  const formHasUnsavedChanges = useSelector(selectFormHasUnsavedChanges);

  // useCallbackPrompt(showCloseModalWithUnsavedDataConfirmation && formHasUnsavedChanges);

  const {
    isOpen: isSaveConfirmationOpen,
    open: onSaveConfirmationOpen,
    close: onSaveConfirmationClose,
  } = useDisclosure({});

  const onCloseAllModals = () => {
    handleClose();
    onSaveConfirmationClose();
  };

  const onClose = () => {
    if (showCloseModalWithUnsavedDataConfirmation && formHasUnsavedChanges) {
      onSaveConfirmationOpen();
    } else {
      onCloseAllModals();
    }
  };

  return (
    <>
      <Dialog
        maxWidth={maxWidth || 'lg'}
        fullWidth
        onClose={onClose}
        open={open}
        sx={{ ...sx, zIndex: '1300 !important', height: '100%' }}
        fullScreen={fullScreen}
        keepMounted={keepMounted}
        {...rest}
      >
        {fixedHeader && (
          <>
            <Stack direction={'row'} p={2} spacing={2} alignItems={'center'} justifyContent={'space-between'}>
              <Stack direction={'column'}>
                {title && <Typography variant="h4">{title}</Typography>}
                {subtitle && (
                  <Typography variant="subtitle1" color={'text.secondary'}>
                    {subtitle}
                  </Typography>
                )}
              </Stack>
              {!title && !subtitle && <Box />}
              {!hideCloseButton && (
                <Box display={'flex'} justifyContent={'end'}>
                  <IconButton onClick={onClose} color={'primary'}>
                    <Iconify icon={'carbon:close-outline'} width={30} height={30} />
                  </IconButton>
                </Box>
              )}
            </Stack>
            <Divider />
          </>
        )}
        {isLoading && (
          <Box py={5} display={'flex'} alignItems={'center'} justifyContent={'center'}>
            <CircularProgress disableShrink />
          </Box>
        )}
        {!isLoading && children && withoutScroll && (
          <Box sx={{ p: 2, maxHeight: fullScreen ? '100%' : '80vh' }}>{children}</Box>
        )}
        {!isLoading && children && !withoutScroll && (
          <Scrollbar sx={{ p: 2, maxHeight: fullScreen ? '100%' : '80vh' }}>{children}</Scrollbar>
        )}
      </Dialog>
      <ConfirmationDialog
        sx={{ zIndex: '1350 !important' }}
        open={isSaveConfirmationOpen}
        onAgree={onCloseAllModals}
        onDisagree={onSaveConfirmationClose}
        onClose={onSaveConfirmationClose}
        text={t('dialog.confirmation.closeModalWithUnsavedData.text')}
        disagreeText={t('button.cancel')}
        agreeText={t('dialog.confirmation.closeModalWithUnsavedData.agree')}
        agreeColor={'error'}
      />
    </>
  );
}
